/**
 * SPIN TENET — OpenFOAM Engine Adapter (Gate 2 Foundation)
 *
 * Implements the EngineAdapter boundary for OpenFOAM (ESI-OpenCFD / Foundation).
 *
 * ARCHITECTURAL RULE:
 * - Owns all OpenFOAM-specific capability checks and validation rules.
 * - The frontend never contains OpenFOAM dictionary syntax or file formats.
 */

import type { EngineDescriptor, RunSpecification, RunLifecycleStatus, CfdResult } from '@contracts/index';
import type { EngineAdapter, AdapterValidationResult, PreparedRunPayload, RunSubmissionResult } from './adapter';

export class OpenFOAMAdapter implements EngineAdapter {
  readonly descriptor: EngineDescriptor = {
    id: 'openfoam',
    displayName: 'OpenFOAM',
    version: 'v2312 / Foundation v11',
    availability: 'available',
    capabilities: {
      supportedBoundaries: [
        'velocity_inlet',
        'pressure_outlet',
        'no_slip_wall',
        'slip_wall',
        'symmetry',
        'open'
      ],
      supportsSlipWalls: true,
      maxDomainDimensions: {
        maxWidth: 50000,
        maxLength: 50000,
        maxHeight: 5000
      },
      minBuildingCount: 1,
      description: 'Incompressible finite-volume Navier-Stokes solver (simpleFoam / pimpleFoam) with snappyHexMesh.'
    },
    adapterId: 'adapter-openfoam-v1',
    description: 'Industrial-grade finite-volume aerodynamic solver suited for arbitrary urban building arrays.'
  };

  validate(specification: RunSpecification): AdapterValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 1. Verify building count
    if (specification.geometry.count < this.descriptor.capabilities.minBuildingCount) {
      errors.push('ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: OpenFOAM requires at least 1 building mass.');
    }

    // 2. Verify domain limits
    const maxDims = this.descriptor.capabilities.maxDomainDimensions!;
    if (
      specification.domain.width > maxDims.maxWidth! ||
      specification.domain.length > maxDims.maxLength! ||
      specification.domain.height > maxDims.maxHeight!
    ) {
      errors.push(
        `ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: Domain exceeds OpenFOAM FVM maximum dimensions (${maxDims.maxWidth}×${maxDims.maxLength}×${maxDims.maxHeight} m).`
      );
    }

    // 3. Verify boundary conditions
    const bc = specification.boundaryConditions;
    if (bc.buildings !== 'no_slip_wall') {
      errors.push(
        'ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: OpenFOAM aerodynamic solver requires building boundaries to be no_slip_wall.'
      );
    }

    if (bc.ground !== 'no_slip_wall' && bc.ground !== 'slip_wall') {
      errors.push(
        'ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: Ground plane must be assigned as no_slip_wall or slip_wall.'
      );
    }

    if (bc.inlet !== 'velocity_inlet') {
      errors.push(
        'ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: Upwind inlet face must be configured as velocity_inlet.'
      );
    }

    if (bc.outlet !== 'pressure_outlet' && bc.outlet !== 'open') {
      errors.push(
        'ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: Downwind outlet face must be configured as pressure_outlet or open.'
      );
    }

    // 4. Physical checks
    if (specification.wind.speed > 80.0) {
      warnings.push(
        'Incompressible assumption may lose validity: wind speed exceeds 80 m/s (Mach > 0.23).'
      );
    }

    return {
      canRepresent: errors.length === 0,
      errors,
      warnings
    };
  }

  async prepare(specification: RunSpecification): Promise<PreparedRunPayload> {
    const val = this.validate(specification);
    if (!val.canRepresent) {
      throw new Error(`OpenFOAM cannot prepare run: ${val.errors.join('; ')}`);
    }

    return {
      caseId: `openfoam-case-${specification.runId}`,
      engine: 'openfoam',
      specification,
      preparedAt: new Date().toISOString()
    };
  }

  private getControlPlaneUrl(): string {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SPIN_TENET_CONTROL_PLANE_URL) {
      return import.meta.env.VITE_SPIN_TENET_CONTROL_PLANE_URL;
    }
    return 'https://desktop-pcu50.tail512eca.ts.net';
  }

  async submit(specification: RunSpecification): Promise<RunSubmissionResult> {
    const val = this.validate(specification);
    if (!val.canRepresent) {
      return {
        success: false,
        runId: specification.runId,
        engine: 'openfoam',
        state: 'FAILED',
        message: val.errors.join('; ')
      };
    }

    try {
      const url = `${this.getControlPlaneUrl()}/api/v1/runs`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ runSpecification: specification })
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        return {
          success: false,
          runId: specification.runId,
          engine: 'openfoam',
          state: 'FAILED',
          message: errJson.error || `Control plane submission failed (HTTP ${response.status})`
        };
      }

      const resData = await response.json();
      const remoteRunId = resData.remoteRunId || resData.runId || specification.runId;
      return {
        success: true,
        runId: specification.runId,
        remoteRunId,
        engine: 'openfoam',
        state: 'QUEUED',
        message: `Run submitted to remote OpenFOAM solver engine (ID: ${remoteRunId}).`
      };
    } catch (err: any) {
      return {
        success: false,
        runId: specification.runId,
        engine: 'openfoam',
        state: 'FAILED',
        message: `Network error connecting to control plane: ${err.message || String(err)}`
      };
    }
  }

  private lastRunErrors = new Map<string, string>();

  async getRunStatus(runId: string): Promise<RunLifecycleStatus> {
    try {
      const url = `${this.getControlPlaneUrl()}/api/v1/runs/${runId}`;
      const response = await fetch(url);
      if (!response.ok) {
        this.lastRunErrors.set(runId, `Control plane returned HTTP ${response.status}`);
        return 'FAILED';
      }
      const data = await response.json();
      if (data.error) {
        this.lastRunErrors.set(runId, String(data.error));
      }
      const rawStatus = (data.status || data.state || 'QUEUED').toUpperCase();
      switch (rawStatus) {
        case 'QUEUED':
        case 'PREPARING':
        case 'RUNNING':
        case 'COMPLETED':
        case 'FAILED':
          return rawStatus as RunLifecycleStatus;
        default:
          return 'RUNNING';
      }
    } catch (err: any) {
      this.lastRunErrors.set(runId, `Network error: ${err.message || String(err)}`);
      return 'FAILED';
    }
  }

  async getRunError(runId: string): Promise<string | undefined> {
    return this.lastRunErrors.get(runId);
  }

  async getResult(runId: string): Promise<CfdResult | null> {
    try {
      const url = `${this.getControlPlaneUrl()}/api/v1/runs/${runId}/result`;
      const response = await fetch(url);
      if (response.status === 200) {
        const cfdResult: CfdResult = await response.json();
        if (cfdResult && cfdResult.provenance) {
          const prov = cfdResult.provenance;
          prov.runId = prov.runId || cfdResult.runId || runId;
          prov.engineId = prov.engineId || 'openfoam';
          prov.engineVersion = prov.engineVersion || cfdResult.engineVersion || 'v2406';
          prov.solver = prov.solver || 'icoFoam';
          prov.caseId = prov.caseId || runId;
          prov.casePath = prov.casePath || `/home/tih50/spin_tenet/experiments/openfoam/${runId}`;
          prov.sourceField = prov.sourceField || 'U';
          prov.sourceTime = prov.sourceTime !== undefined ? prov.sourceTime : '0.05';
          prov.resultExtractionTimestamp = prov.resultExtractionTimestamp || prov.executedAt || new Date().toISOString();
          prov.coordinateSystem = prov.coordinateSystem || cfdResult.coordinateSystem || 'CARTESIAN_METRES_Y_UP';
          prov.dataLocation = prov.dataLocation || cfdResult.fields?.velocity?.dataLocation || 'CELL_CENTERS';
        }
        return cfdResult;
      }
      return null;
    } catch {
      return null;
    }
  }
}
